import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, AuthState } from '@/types'
import { apiService } from '@/services/api.service'
import { auth } from '@/services/firebase.service'
import { signInWithEmailAndPassword, signOut } from 'firebase/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const isMasterAdmin = computed(() => user.value?.is_master_admin || false)
  const isTrialExpired = computed(() => user.value?.is_trial_expired === true)
  const userFullName = computed(() => 
    user.value ? `${user.value.firstName} ${user.value.lastName}` : ''
  )

  // Actions
  async function login(email: string, password: string) {
    loading.value = true
    error.value = null

    try {
      // Firebase authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const firebaseToken = await userCredential.user.getIdToken()

      // Get user profile from backend
      const response = await apiService.get<User>('/user/profile', {
        headers: { Authorization: `Bearer ${firebaseToken}` }
      })

      user.value = response.data
      token.value = firebaseToken

      // Store in localStorage
      localStorage.setItem('auth_token', firebaseToken)
      localStorage.setItem('user', JSON.stringify(response.data))

      // Fetch and store permissions
      await fetchPermissions()

      return true
    } catch (err: any) {
      const firebaseCode = err?.code as string | undefined
      if (firebaseCode === 'auth/unauthorized-domain') {
        error.value = 'This site is not authorized for sign-in. Contact support.'
      } else if (firebaseCode === 'auth/invalid-credential' || firebaseCode === 'auth/wrong-password' || firebaseCode === 'auth/user-not-found') {
        error.value = 'Invalid email or password.'
      } else if (firebaseCode === 'auth/too-many-requests') {
        error.value = 'Too many attempts. Please try again later.'
      } else {
        error.value = err.response?.data?.error || err.message || 'Login failed'
      }
      throw error.value
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await signOut(auth)
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      user.value = null
      token.value = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
    }
  }

  function loadFromStorage() {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = JSON.parse(storedUser)
    }
  }

  function hasPermission(permission: string): boolean {
    return user.value?.permissions.includes(permission) || false
  }

  function hasRole(role: string): boolean {
    return user.value?.role === role
  }

  async function fetchPermissions() {
    try {
      const response = await apiService.get<{ permissions: string[] }>('/user/permissions')
      if (user.value && response.data) {
        user.value.permissions = response.data.permissions
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err)
    }
  }

  // Initialize from localStorage
  loadFromStorage()

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isMasterAdmin,
    isTrialExpired,
    userFullName,
    login,
    logout,
    loadFromStorage,
    hasPermission,
    hasRole,
    fetchPermissions
  }
})
